import React from 'react';
import { Leadership } from '../../types/Leadership';
import { User, Crown } from 'lucide-react';
import { TableColumn } from '../../utils/tableUtils';
import { StatusBadge } from '../shared/AdminTable';

// Define standardized leadership table columns
export const leadershipColumns: TableColumn<Leadership>[] = [
	{
		id: 'name',
		label: 'Name',
		accessor: (leadership) => {
			// Use the same simple image URL logic as working choir images
			const getImageUrl = (image: any): string => {
				if (image?.s3Url) return image.s3Url;
				if (image?.s3Key && image.s3Key.startsWith('data:')) return image.s3Key;
				return '';
			};

			const imageUrl = getImageUrl(leadership.image);

			return (
				<div className="flex items-center gap-2">
					{imageUrl ? (
						<img
							src={imageUrl}
							alt={leadership.name}
							className="w-8 h-8 rounded-full object-cover"
							onError={(e) => {
								(e.target as HTMLImageElement).style.display = 'none';
							}}
						/>
					) : (
						<div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
							<User size={16} className="text-gray-400" />
						</div>
					)}
					<span className="font-medium">{leadership.name}</span>
				</div>
			);
		},
		sortable: true,
	},
	{
		id: 'title',
		label: 'Title',
		accessor: (leadership) => (
			<div className="flex items-center gap-1">
				<Crown size={16} className="text-yellow-500" />
				<span>{leadership.title}</span>
			</div>
		),
		sortable: true,
	},
	{
		id: 'biography',
		label: 'Biography',
		accessor: (leadership) => (
			<div className="max-w-xs truncate" title={leadership.biography}>
				{leadership.biography}
			</div>
		),
		sortable: true,
	},
	{
		id: 'quote',
		label: 'Quote',
		accessor: (leadership) =>
			leadership.quote ? (
				<div
					className="max-w-xs truncate italic text-gray-600"
					title={leadership.quote}
				>
					"{leadership.quote}"
				</div>
			) : (
				<span className="text-gray-400">No quote</span>
			),
		sortable: true,
	},
	{
		id: 'status',
		label: 'Status',
		accessor: (leadership) => (
			<StatusBadge
				status={leadership.isActive ? 'Active' : 'Inactive'}
				type="active"
			/>
		),
		sortable: true,
	},
	{
		id: 'lastUpdated',
		label: 'Last Updated',
		accessor: (leadership) =>
			new Date(leadership.updatedAt).toLocaleDateString(),
		sortable: true,
	},
	{
		id: 'actions',
		label: 'Actions',
		accessor: () => null, // Actions are handled by the AdminTable component
		sortable: false,
	},
];

// Leadership table configuration
export const leadershipTableConfig = {
	entityName: 'Leadership',
	apiEndpoint: '/api/admin/leadership',
	columns: leadershipColumns,
	searchFields: ['name', 'title', 'biography'] as (keyof Leadership)[],
	filterOptions: {
		status: [
			{ value: 'Active', label: 'Active' },
			{ value: 'Inactive', label: 'Inactive' },
		],
	},
	exportFileName: 'leadership',
	defaultVisibleColumns: {
		name: true,
		title: true,
		biography: true,
		quote: false, // Hidden by default since it's optional
		status: true,
		lastUpdated: true,
		actions: true,
	},
};
